/**
 * Frota · Manutenção · OS do ERP.
 *
 * Sinersoft → Ordens de Serviço → placa → SIAG: as OS já lançadas no ERP (oficina
 * própria e notas de terceiros) chegam pelo sync, sem recadastro. Filtra por
 * placa, grupo de veículos, situação e período; o detalhe mostra serviços e
 * peças e registra no plano preventivo o que a OS executou.
 */
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { MaintenanceErpOrderListDto, MaintenanceErpOrderStatusDto } from "@bi/types";
import { api } from "../lib/api";
import { fmtBRL, fmtNumber } from "../lib/format";
import { PageHeader } from "../components/PageHeader";
import { PlateAutocomplete } from "../components/PlateAutocomplete";
import { EmptyState } from "../components/EmptyState";
import { IconWrench } from "../components/icons";
import { GroupSelect } from "../features/maintenance/GroupSelect";

export const ERP_STATUS: Record<MaintenanceErpOrderStatusDto, { label: string; badge: string }> = {
  aberta: { label: "Aberta", badge: "badge-amber" },
  encerrada: { label: "Encerrada", badge: "badge-green" },
  cancelada: { label: "Cancelada", badge: "badge-gray" },
  lancada: { label: "Nota lançada", badge: "badge-blue" },
};

/** Datas do ERP são só dia (00:00 UTC): lidas na string, sem fuso. */
export const diaErp = (iso: string | null) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "—");

export function MaintenanceErpOrdersPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const plate = params.get("plate") ?? "";
  const group = params.get("group") ?? "";
  const status = params.get("status") ?? "";
  const origin = params.get("origin") ?? "";
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  const q = params.get("q") ?? "";

  const lista = useQuery({
    queryKey: ["maintenance", "erp-orders", plate, group, status, origin, from, to, q],
    queryFn: () => {
      const p = new URLSearchParams({ limit: "200" });
      for (const [k, v] of Object.entries({ plate, group, status, origin, from, to, q })) if (v) p.set(k, v);
      return api.get<MaintenanceErpOrderListDto>(`/maintenance/erp-orders?${p}`);
    },
  });

  const setParam = (chave: string, valor: string) => {
    const p = new URLSearchParams(params);
    if (valor) p.set(chave, valor); else p.delete(chave);
    setParams(p, { replace: true });
  };
  const linhas = lista.data?.rows ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="OS do ERP"
        subtitle="Ordens de serviço do Sinersoft (oficina própria e notas de terceiros), ligadas à placa — sem recadastro"
        actions={<Link className="btn-secondary" to="/frota/manutencao/ordens">O.S. do SIAG</Link>}
      />

      <div className="card p-3 flex flex-wrap gap-2 items-center">
        <PlateAutocomplete value={plate} onSelect={(p) => setParam("plate", p)} />
        <GroupSelect value={group} onChange={(g) => setParam("group", g)} />
        <select className="input w-40" value={status} onChange={(e) => setParam("status", e.target.value)} aria-label="Situação">
          <option value="">Toda situação</option>
          {Object.entries(ERP_STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
        </select>
        <select className="input w-44" value={origin} onChange={(e) => setParam("origin", e.target.value)} aria-label="Origem">
          <option value="">Própria e terceiros</option>
          <option value="internal">Oficina própria</option>
          <option value="external">Terceiros</option>
        </select>
        <label className="text-xs text-gray-500 flex items-center gap-1">
          De <input type="date" className="input w-40" value={from} onChange={(e) => setParam("from", e.target.value)} />
        </label>
        <label className="text-xs text-gray-500 flex items-center gap-1">
          até <input type="date" className="input w-40" value={to} onChange={(e) => setParam("to", e.target.value)} />
        </label>
        <input className="input w-40" placeholder="Nº da OS" defaultValue={q}
          onKeyDown={(e) => { if (e.key === "Enter") setParam("q", (e.target as HTMLInputElement).value.trim()); }}
          onBlur={(e) => setParam("q", e.target.value.trim())} />
      </div>

      {lista.data && (
        <p className="text-sm text-gray-600">
          {fmtNumber(lista.data.total)} OS · {fmtBRL(lista.data.totalValue)} no filtro
        </p>
      )}

      <div className="card overflow-x-auto">
        {lista.isLoading ? (
          <p className="p-6 text-sm text-gray-500">Carregando…</p>
        ) : linhas.length === 0 ? (
          <EmptyState
            icon={<IconWrench className="w-5 h-5" />}
            title="Nenhuma OS do ERP neste filtro"
            description="As OS chegam do Sinersoft pelo sync de manutenção (toda madrugada)."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-4 py-2">Nº</th>
                <th className="text-left px-4 py-2">Placa</th>
                <th className="text-left px-4 py-2">Abertura</th>
                <th className="text-left px-4 py-2">Fechamento</th>
                <th className="text-left px-4 py-2">Executor</th>
                <th className="text-right px-4 py-2">KM</th>
                <th className="text-right px-4 py-2">Itens</th>
                <th className="text-left px-4 py-2">Situação</th>
                <th className="text-right px-4 py-2">Valor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {linhas.map((o) => (
                <tr key={o.id} className="hover:bg-surface-muted cursor-pointer" onClick={() => navigate(`/frota/manutencao/erp/${o.id}`)}>
                  <td className="px-4 py-3 font-mono text-gray-900">
                    <Link to={`/frota/manutencao/erp/${o.id}`} onClick={(e) => e.stopPropagation()} className="hover:underline">{o.number}</Link>
                  </td>
                  <td className="px-4 py-3 font-mono">{o.plate ?? "—"}</td>
                  <td className="px-4 py-3 text-gray-600">{diaErp(o.openedAt)}</td>
                  <td className="px-4 py-3 text-gray-600">{diaErp(o.closedAt ?? o.entryDate)}</td>
                  <td className="px-4 py-3 text-gray-600 max-w-[240px] truncate">
                    {o.origin === "internal" ? "Oficina própria" : o.supplierName ?? "Terceiro"}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-600">{o.odometer ? fmtNumber(o.odometer) : "—"}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{o.itemCount}</td>
                  <td className="px-4 py-3"><span className={ERP_STATUS[o.status].badge}>{ERP_STATUS[o.status].label}</span></td>
                  <td className="px-4 py-3 text-right text-gray-900">{fmtBRL(o.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {lista.data && lista.data.total > linhas.length && (
        <p className="text-xs text-gray-500">Mostrando {linhas.length} de {lista.data.total}. Refine pelos filtros.</p>
      )}
    </div>
  );
}
